import { PropsWithChildren, createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { MetaType, ROBOTS } from '@lmp/eduki-routes';
import i18n from 'i18next';
import resetMetaTags from '../utils/reset-meta-tags';

type ProviderMetaType = MetaType & { fromRoute?: boolean };

type DocumentMetaActionsType = {
    updateDocumentMeta: (newMetaData: Partial<MetaType>) => void;
};

export const DocumentMetaActionsContext = createContext<DocumentMetaActionsType | undefined>(undefined);

export const DocumentMetaProvider = ({ meta, children }: { meta: () => MetaType } & PropsWithChildren) => {
    const [metaData, setMetaData] = useState<ProviderMetaType>(
        meta ? { ...meta(), fromRoute: true } : { robots: ROBOTS.NOINDEX_FOLLOW }
    );

    const metaSnapshot = JSON.stringify(meta?.());

    useEffect(() => {
        if (meta) {
            setMetaData({ ...meta(), fromRoute: true });
        }
    }, [metaSnapshot]);

    useEffect(() => {
        resetMetaTags();

        const { title, description, canonical, image, keywords, robots, auto, name, property, isAsync, fromRoute } =
            metaData;
        const isPlaceholder = Boolean(isAsync && fromRoute);
        const showTitle = title && typeof description !== 'function' && !isPlaceholder;
        const showDescription = description && typeof description !== 'function' && !isPlaceholder;

        if (showTitle) {
            document.title = title;
        } else if (!isAsync) {
            document.title = i18n.t('meta:meta.DEFAULT.title');
        }

        const setMetaTag = (name: string, content: string, attr: 'name' | 'property' = 'name') => {
            let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);

            if (!tag) {
                tag = document.createElement('meta');
                tag.setAttribute(attr, name);
                document.head.appendChild(tag);
            }
            tag.setAttribute('content', content);
        };

        if (image) {
            setMetaTag('og:image', image, 'property');
            setMetaTag('og:image:url', image, 'property');
            setMetaTag('og:image:width', '210', 'property');
            setMetaTag('og:image:height', '210', 'property');
            setMetaTag('og:image:type', 'image/png', 'property');
        }

        if (robots) {
            setMetaTag('robots', robots);
        }

        if (!isPlaceholder) {
            setMetaTag('description', showDescription ? description : '');
        }

        setMetaTag('keywords', keywords ?? '');

        const canonicalLink = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

        if (canonical) {
            let link = canonicalLink;

            if (!link) {
                link = document.createElement('link');
                link.rel = 'canonical';
                document.head.appendChild(link);
            }
            link.href = canonical;
        } else if (canonicalLink) {
            canonicalLink.remove();
        }

        if (auto?.ograph) {
            if (showTitle) {
                setMetaTag('og:title', title, 'property');
            }

            if (showDescription) {
                setMetaTag('og:description', description, 'property');
            }

            if (canonical) {
                setMetaTag('og:url', canonical, 'property');
            }
        }

        if (name) {
            Object.entries(name).forEach(([key, value]) => {
                if (value) {
                    setMetaTag(key, value, 'name');
                }
            });
        }

        if (property) {
            Object.entries(property).forEach(([key, value]) => {
                if (value) {
                    setMetaTag(key, value, 'property');
                }
            });
        }
    }, [metaData]);

    const updateDocumentMeta = useCallback((newMetaData: Partial<MetaType>) => {
        setMetaData((netaData) => ({ ...netaData, ...newMetaData, fromRoute: false }));
    }, []);

    const actions = useMemo(() => ({ updateDocumentMeta }), [updateDocumentMeta]);

    return <DocumentMetaActionsContext.Provider value={actions}>{children}</DocumentMetaActionsContext.Provider>;
};
